/**
 * ReplayStore — 重播狀態管理
 *
 * 使用 Zustand 管理重播播放狀態：
 * - 載入重播資料
 * - 播放/暫停/步進/跳轉
 * - 速度控制
 * - 事件追蹤
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getReplay, type ReplayData, type StoredEvent } from '../services/NetworkManager';
import { useAuthStore } from './authStore';

interface ReplayStore {
  // State
  isLoading: boolean;
  error: string | null;
  replayData: ReplayData | null;

  // Playback state
  currentEventIndex: number;
  isPlaying: boolean;
  playbackSpeed: number; // 1, 2, 4
  currentTurn: number;

  // Derived (computed via getters)
  totalEvents: number;
  totalTurns: number;
  currentEvent: StoredEvent | null;
  progress: number; // 0-1

  // Actions
  loadReplay: (gameId: string) => Promise<void>;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  stepForward: () => void;
  stepBackward: () => void;
  jumpToTurn: (turn: number) => void;
  jumpToStart: () => void;
  jumpToEnd: () => void;
  setSpeed: (speed: number) => void;
  setEventIndex: (index: number) => void;
  reset: () => void;
}

/** Auto-play interval ID (module-level for cleanup) */
let playInterval: ReturnType<typeof setInterval> | null = null;

function clearPlayInterval(): void {
  if (playInterval !== null) {
    clearInterval(playInterval);
    playInterval = null;
  }
}

export const useReplayStore = create<ReplayStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      isLoading: false,
      error: null,
      replayData: null,

      currentEventIndex: 0,
      isPlaying: false,
      playbackSpeed: 1,
      currentTurn: 0,

      totalEvents: 0,
      totalTurns: 0,
      currentEvent: null,
      progress: 0,

      loadReplay: async (gameId: string) => {
        set({ isLoading: true, error: null });
        try {
          const token = useAuthStore.getState().token;
          if (!token) {
            set({ isLoading: false, error: '請先登入' });
            return;
          }
          const data = await getReplay(token, gameId);
          const events = data.events;
          const totalTurns = events.length > 0
            ? Math.max(...events.map((e) => e.turn_number))
            : 0;

          set({
            isLoading: false,
            replayData: data,
            currentEventIndex: 0,
            isPlaying: false,
            playbackSpeed: 1,
            currentTurn: events.length > 0 ? events[0].turn_number : 0,
            totalEvents: events.length,
            totalTurns,
            currentEvent: events.length > 0 ? events[0] : null,
            progress: 0,
            error: null,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : '載入重播失敗';
          set({ isLoading: false, error: message });
        }
      },

      play: () => {
        const { totalEvents, currentEventIndex, isPlaying } = get();
        if (isPlaying || currentEventIndex >= totalEvents) return;

        set({ isPlaying: true });

        clearPlayInterval();
        playInterval = setInterval(() => {
          const state = get();
          if (!state.isPlaying || state.currentEventIndex >= state.totalEvents - 1) {
            clearPlayInterval();
            set({ isPlaying: false });
            return;
          }
          // Step forward one event
          get().stepForward();
        }, 1000 / get().playbackSpeed);
      },

      pause: () => {
        clearPlayInterval();
        set({ isPlaying: false });
      },

      togglePlay: () => {
        const { isPlaying } = get();
        if (isPlaying) {
          get().pause();
        } else {
          get().play();
        }
      },

      stepForward: () => {
        const { replayData, currentEventIndex, totalEvents } = get();
        if (!replayData || currentEventIndex >= totalEvents - 1) return;

        const newIndex = currentEventIndex + 1;
        const events = replayData.events;
        const event = events[newIndex] as StoredEvent | undefined;

        set({
          currentEventIndex: newIndex,
          currentEvent: event,
          currentTurn: event ? event.turn_number : get().currentTurn,
          progress: totalEvents > 1 ? newIndex / (totalEvents - 1) : 0,
        });
      },

      stepBackward: () => {
        const { replayData, currentEventIndex } = get();
        if (!replayData || currentEventIndex <= 0) return;

        const newIndex = currentEventIndex - 1;
        const events = replayData.events;
        const event = events[newIndex] as StoredEvent | undefined;
        const totalEvents = get().totalEvents;

        set({
          currentEventIndex: newIndex,
          currentEvent: event,
          currentTurn: event ? event.turn_number : get().currentTurn,
          progress: totalEvents > 1 ? newIndex / (totalEvents - 1) : 0,
        });
      },

      jumpToTurn: (turn: number) => {
        const { replayData, totalEvents } = get();
        if (!replayData) return;

        const events = replayData.events;
        // Find first event of the target turn
        let targetIndex = events.findIndex((e) => e.turn_number >= turn);
        if (targetIndex === -1) {
          targetIndex = events.length - 1;
        }

        const event = events[targetIndex] as StoredEvent | undefined;
        set({
          currentEventIndex: targetIndex,
          currentEvent: event,
          currentTurn: event ? event.turn_number : turn,
          progress: totalEvents > 1 ? targetIndex / (totalEvents - 1) : 0,
        });
      },

      jumpToStart: () => {
        const { replayData } = get();
        if (!replayData) return;

        const events = replayData.events;
        const event = events.length > 0 ? events[0] : null;

        set({
          currentEventIndex: 0,
          currentEvent: event,
          currentTurn: event ? event.turn_number : 0,
          progress: 0,
        });

        // If playing, pause
        if (get().isPlaying) {
          get().pause();
        }
      },

      jumpToEnd: () => {
        const { replayData } = get();
        if (!replayData) return;

        const events = replayData.events;
        const lastIndex = events.length - 1;
        const event = events.length > 0 ? events[lastIndex] : null;

        set({
          currentEventIndex: Math.max(0, lastIndex),
          currentEvent: event,
          currentTurn: event ? event.turn_number : get().totalTurns,
          progress: 1,
        });

        // If playing, pause
        if (get().isPlaying) {
          get().pause();
        }
      },

      setSpeed: (speed: number) => {
        set({ playbackSpeed: speed });

        // If currently playing, restart interval with new speed
        if (get().isPlaying) {
          clearPlayInterval();
          playInterval = setInterval(() => {
            const state = get();
            if (!state.isPlaying || state.currentEventIndex >= state.totalEvents - 1) {
              clearPlayInterval();
              set({ isPlaying: false });
              return;
            }
            get().stepForward();
          }, 1000 / speed);
        }
      },

      setEventIndex: (index: number) => {
        const { replayData, totalEvents } = get();
        if (!replayData) return;

        const events = replayData.events;
        const clampedIndex = Math.max(0, Math.min(index, events.length - 1));
        const event = events[clampedIndex] as StoredEvent | undefined;

        set({
          currentEventIndex: clampedIndex,
          currentEvent: event,
          currentTurn: event ? event.turn_number : get().currentTurn,
          progress: totalEvents > 1 ? clampedIndex / (totalEvents - 1) : 0,
        });
      },

      reset: () => {
        clearPlayInterval();
        set({
          isLoading: false,
          error: null,
          replayData: null,
          currentEventIndex: 0,
          isPlaying: false,
          playbackSpeed: 1,
          currentTurn: 0,
          totalEvents: 0,
          totalTurns: 0,
          currentEvent: null,
          progress: 0,
        });
      },
    }),
    { name: 'ReplayStore' },
  ),
);
